import React, {
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useSize } from 'ahooks';
import IconFont from '$/components/IconFont';
import ImageUploader from '@/components/ImageUploader';
import { message } from '$/components/Message';
import useImageUpload from '@/hooks/useImageUpload';
import { useLanguageStore } from '$/store/languageStore';
import useAIEnhanceStore, {
  toggleBatchImageSelected,
  setPreviewImageIndex,
  addBatchImages,
  removeBatchImage,
  updateBatchImage,
  setBatchImages,
  setBatchReuploadPending,
} from '@/store/useAIEnhanceStore';
import { v4 as uuid } from 'uuid';
import { useShallow } from 'zustand/react/shallow';
import { useAiToolUploadLoginGuard } from '@/pages/--roboneo/utils/uploadLoginGuard';
import { trackAiToolUploadClick, trackAiToolUploadSuccess } from '@/pages/--roboneo/utils/aiToolUploadTrack';
import BatchProcessCard from '@/pages/--roboneo/ai_eraser/components/BatchList/BatchProcessCard';
import {
  createUploadedMediaBatchItem,
  filterUploadedByMediacheck,
} from '../../../ai_eraser/utils/mediaCheckAfterUpload';
import {
  AssetToolType,
  autoSaveAiToolUploadedAssets,
} from '../../../ai_eraser/utils/assetLibrary';
import { isVideoFile } from '../../../ai_eraser/utils/mediaType';
import styles from './index.module.scss';

const MIN_CARD_WIDTH = 180;
const MAX_CARD_WIDTH = 240;
const GRID_GAP = 12;
const MAX_BATCH_COUNT = 30;
const ACCEPT_FORMATS = '.png,.jpg,.jpeg,.mp4,.mov';
const UPLOAD_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'video/mp4', 'video/quicktime'];
const VIDEO_MAX_SIZE_MB = 500;
const IMAGE_MAX_SIZE_MB = 30;

function getGridContentWidth(el: HTMLDivElement | null) {
  if (!el || typeof window === 'undefined') return 0;
  const style = window.getComputedStyle(el);
  const paddingLeft = Number.parseFloat(style.paddingLeft) || 0;
  const paddingRight = Number.parseFloat(style.paddingRight) || 0;
  return Math.max(0, el.clientWidth - paddingLeft - paddingRight);
}

function getColumns(containerWidth: number) {
  if (containerWidth <= 0) return 1;

  const minColumnsForMax = Math.max(
    1,
    Math.ceil((containerWidth + GRID_GAP) / (MAX_CARD_WIDTH + GRID_GAP)),
  );
  const maxColumnsForMin = Math.max(
    1,
    Math.floor((containerWidth + GRID_GAP) / (MIN_CARD_WIDTH + GRID_GAP)),
  );

  if (minColumnsForMax <= maxColumnsForMin) {
    return minColumnsForMax;
  }
  return maxColumnsForMin;
}

export default function BatchList() {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const guardUploadEntry = useAiToolUploadLoginGuard();
  const containerRef = useRef<HTMLDivElement>(null);
  const size = useSize(containerRef);
  const {
    batchImages, batchProcessingMode, batchEraserLoading, batchReuploadSignal, batchReuploadPending,
  } = useAIEnhanceStore(useShallow((state) => ({
    batchImages: state.batchImages,
    batchProcessingMode: state.batchProcessingMode,
    batchEraserLoading: state.batchEraserLoading,
    batchReuploadSignal: state.batchReuploadSignal,
    batchReuploadPending: state.batchReuploadPending,
  })));

  const columns = useMemo(
    () => getColumns(getGridContentWidth(containerRef.current)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [size?.width],
  );

  const { uploadImages, uploading } = useImageUpload({
    maxCount: MAX_BATCH_COUNT,
    acceptedTypes: UPLOAD_ACCEPTED_TYPES,
    maxFileSize: (file) => (isVideoFile(file) ? VIDEO_MAX_SIZE_MB : IMAGE_MAX_SIZE_MB),
    afterUpload: async (images, files) => {
      const safeImages = await filterUploadedByMediacheck(images, files, { isCheckSafe: true });
      autoSaveAiToolUploadedAssets(safeImages, files, ({ materialType }) => (
        materialType === 'video' ? AssetToolType.VIDEO_HD : AssetToolType.IMAGE_UPSCALER
      ));
      return safeImages;
    },
    onSuccess: () => {},
    onError: (err) => {
      message.error(err?.message || languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_upload_fail_text));
    },
  });

  /**
   * 上传卡片打开文件选择框时立即上报点击，不等待用户选中文件。
   */
  const handleUploadOpen = useCallback(() => {
    const pending = useAIEnhanceStore.getState().batchReuploadPending;
    trackAiToolUploadClick({
      func: 'change_to_hd',
      source: pending ? 'reload' : 'batch_continue_load',
    });
  }, []);

  /**
   * 批量上传成功后写入列表，并用真实文件补齐成功埋点字段。
   */
  const handleBatchFilesSelected = async (files: File[]) => {
    if (!guardUploadEntry()) return;
    const pending = useAIEnhanceStore.getState().batchReuploadPending;
    const result = await uploadImages(files);
    if (!result?.length) return;
    if (files[0]) {
      trackAiToolUploadSuccess({
        func: 'change_to_hd',
        source: pending ? 'reload' : 'batch_continue_load',
        file: files[0],
      });
    }
    const newItems = await Promise.all(
      result.map((img, index) => createUploadedMediaBatchItem(img, files, index, `${uuid()}`)),
    );
    if (!newItems.length) return;
    if (pending) {
      setBatchImages(newItems);
      setBatchReuploadPending(false);
      setPreviewImageIndex(null);
    } else {
      addBatchImages(newItems);
    }
  };

  const isUploadDisabled = batchImages.length >= MAX_BATCH_COUNT
    || uploading
    || batchEraserLoading
    || batchProcessingMode;

  return (
    <div className={styles.batchListContainer}>
      <div
        ref={containerRef}
        className={styles.batchList}
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        <ImageUploader
          onFilesSelected={(files) => {
            handleBatchFilesSelected(files).catch(() => {});
          }}
          beforeOpen={guardUploadEntry}
          onOpen={handleUploadOpen}
          maxCount={MAX_BATCH_COUNT}
          currentCount={batchReuploadPending ? 0 : batchImages.length}
          disabled={uploading}
          accept={ACCEPT_FORMATS}
          title={languageStore.smartcatTranslationText((v) => v[' upload_something'])}
          className={isUploadDisabled ? styles.uploadCardDisabled : ''}
          openKey={batchReuploadSignal}
        >
          <div className={styles.uploadCard}>
            <div className={styles.uploadContent}>
              <IconFont
                name="plusBold"
                fontSize={24}
                className={styles.uploadIcon}
              />
              <span className={styles.uploadText}>
                {languageStore.smartcatTranslationText(
                  (v) => v.ai_enhance_upload,
                )}
              </span>
            </div>
          </div>
        </ImageUploader>
        {batchImages.map((item, index) => (
          <BatchProcessCard
            key={item.id}
            item={item}
            selectable={batchProcessingMode}
            onToggleSelect={() => toggleBatchImageSelected(item.id)}
            onPreviewClick={() => setPreviewImageIndex(index)}
            onDelete={() => removeBatchImage(item.id)}
            onResultImageLoad={() => updateBatchImage(item.id, { resultImageLoading: false })}
          />
        ))}
      </div>
    </div>
  );
}
